import React, { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import useStyles from 'isomorphic-style-loader/useStyles';
import Button from '@material-ui/core/Button';
import CancelIcon from '@material-ui/icons/Cancel';
import Tooltip from '@material-ui/core/Tooltip';
import WallpaperIcon from '@material-ui/icons/Wallpaper';

import {
  MAX_IMG_FILE_SIZE,
  MIN_IMG_FILE_SIZE,
  SUPPORTED_IMG_FORMATS,
  acceptedFormatsInASentence,
} from 'constants/index';
import Skeleton from 'react-loading-skeleton';

import { isApp } from 'services/environment';

import ConfirmDeletionDialog from 'components/ConfirmDeletionDialog';

import useDisableSwiping from 'hooks/useDisableSwiping';

// app: assets
import CssActionSheet from './ImageUploader.scss';

function ImageUploader(props) {
  const {
    isUploading,
    uploadError,
    isOpen,
    isLoading,
    onImageSelected,
    onRemoveExistingImage,
    className,
    maxFileSize,
    minFileSize,
    acceptedFormats,
    onValidationError,
    existingImage,
  } = props;
  useStyles(CssActionSheet);

  const wholeElementRef = useRef(null);
  const fileInputRef = useRef(null);
  const [isItemOver, setIsItemOver] = useState(false);
  const [confirmDeleteDialogOpen, setConfirmDeleteDialogOpen] = useState(false);

  const [selectedImage, setSelectedImage] = useState(existingImage);

  const [validationError, setValidationError] = useState('');

  const [allowedExtensions, allowedMIMES] = useMemo(
    () => [
      acceptedFormats.map(format => `.${format.toLowerCase()}`),
      acceptedFormats.map(format => `image/${format.toLowerCase()}`),
    ],
    [acceptedFormats],
  );

  useDisableSwiping(wholeElementRef);

  useEffect(
    () => {
      if (existingImage !== selectedImage) {
        setSelectedImage(existingImage);
      }
    },
    [existingImage],
  );

  useEffect(
    () => {
      if (!isOpen && selectedImage) {
        setSelectedImage(null);

        if (onImageSelected) {
          onImageSelected(null);
        }
      }
    },
    [selectedImage, isOpen, onImageSelected],
  );

  const bytesToMB = bytes => bytes / (1024 * 1024);

  const validateImageFile = file => {
    // Check if file is selected
    if (!file || !fileInputRef.current) {
      return 'No file selected';
    }

    // Check file extension
    if (
      !allowedExtensions.some(extension => file.name.toLowerCase().endsWith(extension)) ||
      !allowedMIMES.includes(file.type)
    ) {
      fileInputRef.current.value = '';

      return `Unsupported format - please upload as ${acceptedFormatsInASentence(acceptedFormats)}`;
    }

    if (file.size > maxFileSize) {
      fileInputRef.current.value = '';
      return `File is too large (max file size ${bytesToMB(maxFileSize)}MB)`;
    }

    if (file.size < minFileSize) {
      fileInputRef.current.value = '';
      return `File is too small (min file size ${bytesToMB(minFileSize)}MB)`;
    }

    return '';
  };

  const validateAndSelectImage = file => {
    const imageValidationError = validateImageFile(file);

    if (imageValidationError && onValidationError) {
      onValidationError(imageValidationError);
      setValidationError(imageValidationError);
      return;
    }

    if (file && !imageValidationError) {
      const reader = new FileReader();
      reader.onload = e => {
        setSelectedImage(e.target.result);
        setValidationError('');

        if (onImageSelected) {
          onImageSelected(file);
        }
      };
      reader.readAsDataURL(file);
    }
  };

  const handleInputFileSelect = inputSelectEvent => {
    inputSelectEvent.preventDefault();
    validateAndSelectImage(inputSelectEvent.target.files[0]);
  };

  const handleDragOver = dragOverEvent => {
    dragOverEvent.preventDefault();
    if (isUploading || selectedImage) {
      return;
    }

    if (!isItemOver) {
      setIsItemOver(true);
    }
  };

  const handleDragLeave = dragLeaveEvent => {
    dragLeaveEvent.preventDefault();
    if (isUploading || selectedImage) {
      return;
    }

    if (isItemOver) {
      setIsItemOver(false);
    }
  };

  const handleDrop = dropEvent => {
    dropEvent.preventDefault();
    if (isUploading || selectedImage) {
      return;
    }

    if (isItemOver) {
      setIsItemOver(false);
    }

    validateAndSelectImage(dropEvent.dataTransfer.files[0]);
  };

  const handleDropZoneClick = () => {
    if (isUploading || !fileInputRef.current || selectedImage) {
      return;
    }

    fileInputRef.current.click();
  };

  const handleRemoveImage = isConfirmed => {
    if (isUploading || !fileInputRef.current) {
      return;
    }

    if (existingImage && isConfirmed === undefined) {
      setConfirmDeleteDialogOpen(true);
      return;
    }

    if (existingImage && isConfirmed && onRemoveExistingImage) {
      onRemoveExistingImage();
    }

    fileInputRef.current.value = '';
    setSelectedImage(null);

    if (onImageSelected) {
      onImageSelected(null);
    }
  };

  const handleCancelDelete = () => {
    setConfirmDeleteDialogOpen(false);
  };

  const handleConfirmDelete = () => {
    setConfirmDeleteDialogOpen(false);
    handleRemoveImage(true);
  };

  const uploadPlaceholder = isApp() ? 'Add a photo' : 'Drag and drop or click to upload a photo';

  return (
    <>
      <div
        ref={wholeElementRef}
        className={`image-uploader ${className} image-uploader-${isOpen ? 'open' : 'closed'}`}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        onDragLeave={handleDragLeave}
      >
        {isLoading && <Skeleton width="240px" height="160px" />}

        {!isLoading &&
          !selectedImage && (
            <button
              type="button"
              onClick={handleDropZoneClick}
              className={`image-uploader-zone-button image-uploader-zone-button-${
                isItemOver ? 'over' : 'not-over'
              }`}
            >
              <WallpaperIcon />
              <span>{uploadPlaceholder}</span>
            </button>
          )}

        {selectedImage && (
          <div className="image-uploader-preview-image">
            <img src={selectedImage} alt="Preview" />
            <div
              className={`image-uploader-preview-image-upload-status ${isUploading &&
                'image-uploader-preview-image-upload-status-opacity'}`}
            >
              <p>Uploading...</p>
            </div>

            <Tooltip title="Delete review image" aria-label="delete">
              <Button
                size="small"
                type="button"
                disabled={isUploading}
                className="image-uploader-preview-image-delete-button"
                color="primary"
                onClick={() => {
                  handleRemoveImage();
                }}
              >
                <CancelIcon fontSize="large" />
              </Button>
            </Tooltip>
          </div>
        )}
        <input
          type="file"
          ref={fileInputRef}
          onChange={handleInputFileSelect}
          accept={allowedMIMES.join(', ')}
          className="image-uploader-hidden-input"
        />
        {(validationError || uploadError) && (
          <p className="image-uploader-validation-error">{validationError || uploadError}</p>
        )}
      </div>
      <ConfirmDeletionDialog
        title="Are you sure ?"
        body=""
        proceedText="Remove image"
        open={confirmDeleteDialogOpen}
        onClose={handleCancelDelete}
        onAgree={handleConfirmDelete}
      />
    </>
  );
}

ImageUploader.propTypes = {
  isUploading: PropTypes.bool,
  isOpen: PropTypes.bool,
  isLoading: PropTypes.bool,
  onImageSelected: PropTypes.func,
  onValidationError: PropTypes.func,
  onRemoveExistingImage: PropTypes.func,
  className: PropTypes.string,
  existingImage: PropTypes.string,
  uploadError: PropTypes.string,
  maxFileSize: PropTypes.number,
  minFileSize: PropTypes.number,
  acceptedFormats: PropTypes.arrayOf(PropTypes.string),
};

ImageUploader.defaultProps = {
  isUploading: false,
  isOpen: false,
  isLoading: false,
  onImageSelected: () => {},
  onValidationError: () => {},
  onRemoveExistingImage: () => {},
  className: '',
  uploadError: '',
  existingImage: null,
  maxFileSize: MAX_IMG_FILE_SIZE,
  minFileSize: MIN_IMG_FILE_SIZE,
  acceptedFormats: SUPPORTED_IMG_FORMATS,
};

export default ImageUploader;
